import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./RepeatedDrawings.css";

const SYMBOLS = [
  "🐶",
  "🐱",
  "🐭",
  "🐰",
  "🦊",
  "🐻",
  "🐼",
  "🐸",
  "🐵",
  "🐧",
  "🦁",
  "🐯",
  "🍎",
  "🍌",
  "🍇",
  "🍓",
  "🍊",
  "🍐",
  "🚗",
  "🚲",
  "🚀",
  "✈️",
  "⚽",
  "🏀",
  "🎈",
  "🎁",
  "⭐",
  "🌸",
  "🦋",
  "📚",
  "🔑",
  "🧸",
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    uniqueSymbols: 5,
    repeatedGroups: 1,
    repetitionsPerGroup: 2,
    columns: 3,
  },
  medium: {
    label: "Media",
    totalRounds: 10,
    uniqueSymbols: 7,
    repeatedGroups: 2,
    repetitionsPerGroup: 2,
    columns: 4,
  },
  hard: {
    label: "Difícil",
    totalRounds: 12,
    uniqueSymbols: 9,
    repeatedGroups: 3,
    repetitionsPerGroup: 2,
    columns: 5,
  },
};

function shuffle(list) {
  const copy = [...list];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));

    [copy[index], copy[randomIndex]] = [
      copy[randomIndex],
      copy[index],
    ];
  }

  return copy;
}

function createRound(config) {
  const selectedSymbols = shuffle(SYMBOLS).slice(
    0,
    config.uniqueSymbols
  );

  const repeatedSymbols = shuffle(selectedSymbols).slice(
    0,
    config.repeatedGroups
  );

  const items = [];

  selectedSymbols.forEach((symbol, symbolIndex) => {
    const repetitions = repeatedSymbols.includes(symbol)
      ? config.repetitionsPerGroup
      : 1;

    for (let index = 0; index < repetitions; index += 1) {
      items.push({
        id: `${symbolIndex}-${index}-${symbol}`,
        symbol,
        isRepeated: repeatedSymbols.includes(symbol),
      });
    }
  });

  return {
    items: shuffle(items),
    repeatedSymbols,
  };
}

export default function RepeatedDrawings() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctRounds, setCorrectRounds] = useState(0);
  const [correctSelections, setCorrectSelections] = useState(0);
  const [wrongSelections, setWrongSelections] = useState(0);
  const [omissions, setOmissions] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(() => {
    if (!totalRounds) return 0;

    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const totalErrors = wrongSelections + omissions;

  const accuracy =
    correctSelections + totalErrors > 0
      ? Math.round(
          (correctSelections /
            (correctSelections + totalErrors)) *
            100
        )
      : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce(
            (total, response) =>
              total + response.reactionTime,
            0
          ) / responses.length
        )
      : 0;

  const totalTime =
    startTime && endTime
      ? Math.round((endTime - startTime) / 1000)
      : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficulty = selectedDifficulty || "easy"
  ) => {
    const nextConfig =
      GAME_CONFIG[difficulty] || GAME_CONFIG.easy;

    const now = Date.now();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(createRound(nextConfig));
    setSelectedIds([]);

    setCorrectRounds(0);
    setCorrectSelections(0);
    setWrongSelections(0);
    setOmissions(0);
    setResponses([]);

    setStarted(true);
    setFinished(false);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
  };

  const toggleItem = (itemId) => {
    if (!started || finished) return;

    setSelectedIds((previous) =>
      previous.includes(itemId)
        ? previous.filter((id) => id !== itemId)
        : [...previous, itemId]
    );
  };

  const confirmRound = () => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;

    const repeatedItems = round.items.filter(
      (item) => item.isRepeated
    );

    const selectedItems = round.items.filter((item) =>
      selectedIds.includes(item.id)
    );

    const roundCorrectSelections = selectedItems.filter(
      (item) => item.isRepeated
    ).length;

    const roundWrongSelections = selectedItems.filter(
      (item) => !item.isRepeated
    ).length;

    const roundOmissions =
      repeatedItems.length - roundCorrectSelections;

    const isPerfectRound =
      roundWrongSelections === 0 && roundOmissions === 0;

    setResponses((previous) => [
      ...previous,
      {
        round: roundIndex + 1,
        repeatedSymbols: round.repeatedSymbols,
        selectedSymbols: selectedItems.map(
          (item) => item.symbol
        ),
        correctSelections: roundCorrectSelections,
        wrongSelections: roundWrongSelections,
        omissions: roundOmissions,
        isCorrect: isPerfectRound,
        reactionTime,
      },
    ]);

    if (isPerfectRound) {
      setCorrectRounds((previous) => previous + 1);
    }

    setCorrectSelections(
      (previous) => previous + roundCorrectSelections
    );

    setWrongSelections(
      (previous) => previous + roundWrongSelections
    );

    setOmissions(
      (previous) => previous + roundOmissions
    );

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= config.totalRounds) {
      setStarted(false);
      setFinished(true);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRoundIndex);
    setRound(createRound(config));
    setSelectedIds([]);
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setRound(null);
    setSelectedIds([]);
    setRoundIndex(0);
    setStarted(false);
    setFinished(false);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Dibujos repetidos"
        description="Observa todos los dibujos y selecciona todos los que aparezcan más de una vez."
        areas={[
          "Atención selectiva",
          "Atención sostenida",
          "Rastreo visual",
          "Discriminación visual",
        ]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="repeated-drawings-page">
      <div className="repeated-drawings-card">
        <header className="repeated-drawings-header">
          <div>
            <h1>Dibujos repetidos</h1>

            <p>
              Dificultad:{" "}
              <strong>{config?.label}</strong>
            </p>
          </div>

          <button
            type="button"
            onClick={() =>
              startGame(selectedDifficulty)
            }
          >
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="repeated-drawings-instruction">
              <h2>Selecciona todos los dibujos repetidos</h2>

              <p>
                Puede haber más de un dibujo repetido.
              </p>
            </section>

            <section
              className="repeated-drawings-grid"
              style={{
                gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
              }}
            >
              {round.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`repeated-drawing-item ${
                    selectedIds.includes(item.id)
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => toggleItem(item.id)}
                  aria-pressed={selectedIds.includes(
                    item.id
                  )}
                >
                  {item.symbol}
                </button>
              ))}
            </section>

            <button
              type="button"
              className="confirm-repeated-button"
              onClick={confirmRound}
              disabled={selectedIds.length === 0}
            >
              Confirmar selección
            </button>

            <section className="repeated-drawings-score">
              <span>
                Rondas correctas: {correctRounds}
              </span>

              <span>
                Selecciones correctas:{" "}
                {correctSelections}
              </span>

              <span>
                Errores: {wrongSelections}
              </span>

              <span>
                Omisiones: {omissions}
              </span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                {
                  label: "Rondas correctas",
                  value: correctRounds,
                },
                {
                  label: "Selecciones correctas",
                  value: correctSelections,
                },
                {
                  label: "Errores",
                  value: wrongSelections,
                },
                {
                  label: "Omisiones",
                  value: omissions,
                },
                {
                  label: "Precisión",
                  value: `${accuracy}%`,
                },
                {
                  label: "Tiempo medio",
                  value: `${averageReactionTime} ms`,
                },
                {
                  label: "Tiempo total",
                  value: `${totalTime}s`,
                },
              ]}
              onRepeat={() =>
                startGame(selectedDifficulty)
              }
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation
              interpretation={interpretation}
            />
          </>
        )}
      </div>
    </div>
  );
}